import {
  TAGS_SPECIALTY,
  TAGS_KNOWLEDGE_CONTENT_TYPE,
  TAGS_TARGET_AUDIENCE,
} from 'shared/constants/tags.constants';
import en from 'shared/constants/languages/en.lang.json';

/**
 * Per-organization search ranking rules — see back-office/SEARCH_RANKING_RULES.md.
 * Rules live at `internalConfigurations.search.rules` and are written as a whole
 * array; this module holds the enums, the vocabulary mappers and the
 * form <-> API serialization.
 */

export const ITEM_PATHS = {
  LABELS: 'LABELS',
  TYPE: 'TYPE',
  SPECIALTY: 'SPECIALTY',
  SUBSPECIALTY: 'SUBSPECIALTY',
  CONTENT_TYPE: 'CONTENT_TYPE',
  TARGET_AUDIENCE: 'TARGET_AUDIENCE',
};

export const USER_PATHS = {
  LOCATION: 'professional.location',
  SPECIALTY: 'professional.specialty',
  DIVISION: 'professional.division',
  UNIT: 'professional.unit',
  DEPARTMENT: 'professional.department',
};

export const ITEM_PATH_LABELS = {
  [ITEM_PATHS.LABELS]: 'Labels',
  [ITEM_PATHS.TYPE]: 'Kind of entity',
  [ITEM_PATHS.SPECIALTY]: 'Specialty tag',
  [ITEM_PATHS.SUBSPECIALTY]: 'Subspecialty tag',
  [ITEM_PATHS.CONTENT_TYPE]: 'Content type tag',
  [ITEM_PATHS.TARGET_AUDIENCE]: 'Target audience tag',
};

/** Only these hold an array on the result, so only they can use `vocabulary`. */
export const MULTI_VALUED_ITEM_PATHS = new Set([ITEM_PATHS.LABELS, ITEM_PATHS.TARGET_AUDIENCE]);

export const ENTITY_TYPES = [
  'knowledgeItems',
  'knowledgeCategories',
  'labValues',
  'labCategories',
  'admissions',
  'contact',
  'users',
];

export const MULTIPLIER_MIN = 0.2;
export const MULTIPLIER_MAX = 200;
export const EXCLUDE = 'EXCLUDE';

export const BUCKETS = ['match', 'unassigned', 'other'];

export const BUCKET_LABELS = {
  match: 'Match',
  unassigned: 'Unassigned',
  other: 'Other',
};

export const BUCKET_DESCRIPTIONS = {
  match: 'Has one of the values the rule looks for.',
  unassigned: 'Has no value at all for that field.',
  other: 'Has a value, but not one the rule looks for.',
};

export const OUTCOME_MODES = {
  NO_CHANGE: 'NO_CHANGE',
  MULTIPLIER: 'MULTIPLIER',
  EXCLUDE: 'EXCLUDE',
};

/**
 * Which vocabulary a rule's `values` must be drawn from, per `itemPath`.
 * `labels` resolves at runtime from GET /v1/labels — it is per-organization.
 */
export const VALUE_SOURCE_BY_ITEM_PATH = {
  [ITEM_PATHS.LABELS]: 'labels',
  [ITEM_PATHS.TYPE]: 'entityTypes',
  [ITEM_PATHS.SPECIALTY]: 'specialtyTags',
  [ITEM_PATHS.SUBSPECIALTY]: 'specialtyTags',
  [ITEM_PATHS.CONTENT_TYPE]: 'contentTypeTags',
  [ITEM_PATHS.TARGET_AUDIENCE]: 'targetAudienceTags',
};

export const STATIC_VOCABULARIES = {
  entityTypes: ENTITY_TYPES,
  specialtyTags: Object.values(TAGS_SPECIALTY),
  contentTypeTags: Object.values(TAGS_KNOWLEDGE_CONTENT_TYPE),
  targetAudienceTags: Object.values(TAGS_TARGET_AUDIENCE),
};

/**
 * userPath -> profile field key. `professional.location` is deliberately absent:
 * it stores organizationLabels codes, not profile-field options.
 */
export const PROFILE_FIELD_KEY_BY_USER_PATH = {
  [USER_PATHS.SPECIALTY]: 'specialty',
  [USER_PATHS.DIVISION]: 'division',
  [USER_PATHS.UNIT]: 'unit',
  [USER_PATHS.DEPARTMENT]: 'department',
};

/** The one pairing whose two sides are guaranteed to share a vocabulary. */
export const SAFE_PAIRING = { userPath: USER_PATHS.LOCATION, itemPath: ITEM_PATHS.LABELS };

const TAG_LABELS = en.TAGS ?? {};

/** Tag codes are what actually match, so always show the code alongside the name. */
export const tagDisplayName = code => TAG_LABELS[code] ?? code;

// ---------- form <-> API ----------

export const outcomeToForm = outcome => {
  if (outcome === EXCLUDE) return { mode: OUTCOME_MODES.EXCLUDE, multiplier: '' };
  const numeric = Number(outcome);
  if (!outcome || numeric === 1 || Number.isNaN(numeric)) {
    return { mode: OUTCOME_MODES.NO_CHANGE, multiplier: '' };
  }
  return { mode: OUTCOME_MODES.MULTIPLIER, multiplier: String(numeric) };
};

export const outcomeToApi = ({ mode, multiplier }) => {
  if (mode === OUTCOME_MODES.EXCLUDE) return EXCLUDE;
  if (mode === OUTCOME_MODES.MULTIPLIER) return Number(multiplier);
  return 1;
};

export const TARGET_MODES = { USER: 'USER', STATIC: 'STATIC' };

/**
 * The profile-field default comes from the org's own configured fields, not a
 * constant: a rule reading a field this organization does not use is accepted on
 * write and then never fires, so an unusable default must not be reachable.
 */
export const emptyRuleFormValues = (userPathOptions = []) => ({
  key: '',
  enabled: true,
  itemPath: ITEM_PATHS.LABELS,
  targetMode: TARGET_MODES.USER,
  userPath: userPathOptions[0]?.value ?? '',
  values: [],
  vocabulary: [],
  workspaceIds: [],
  match: { mode: OUTCOME_MODES.NO_CHANGE, multiplier: '' },
  unassigned: { mode: OUTCOME_MODES.NO_CHANGE, multiplier: '' },
  other: { mode: OUTCOME_MODES.NO_CHANGE, multiplier: '' },
});

export const toFormValues = (rule, userPathOptions = []) => {
  if (!rule) return emptyRuleFormValues(userPathOptions);
  return {
    key: rule.key ?? '',
    enabled: rule.enabled ?? true,
    itemPath: rule.itemPath ?? ITEM_PATHS.LABELS,
    targetMode: rule.userPath ? TARGET_MODES.USER : TARGET_MODES.STATIC,
    userPath: rule.userPath ?? userPathOptions[0]?.value ?? '',
    values: rule.values ?? [],
    vocabulary: rule.vocabulary ?? [],
    workspaceIds: rule.workspaceIds ?? [],
    match: outcomeToForm(rule.match),
    unassigned: outcomeToForm(rule.unassigned),
    other: outcomeToForm(rule.other),
  };
};

/**
 * Build the wire rule from scratch. The backend rejects unrecognised keys, so
 * form-only state (`targetMode`, the unused half of userPath/values) must never
 * leak into the payload.
 */
export const toApiRule = values => {
  const rule = {
    key: values.key.trim(),
    enabled: values.enabled,
    itemPath: values.itemPath,
    match: outcomeToApi(values.match),
    unassigned: outcomeToApi(values.unassigned),
    other: outcomeToApi(values.other),
  };

  if (values.targetMode === TARGET_MODES.USER) {
    rule.userPath = values.userPath;
  } else {
    rule.values = values.values;
  }

  if (MULTI_VALUED_ITEM_PATHS.has(values.itemPath) && values.vocabulary?.length) {
    rule.vocabulary = values.vocabulary;
  }
  if (values.workspaceIds?.length) {
    rule.workspaceIds = values.workspaceIds;
  }

  return rule;
};

const RULE_KEYS = [
  'key',
  'enabled',
  'itemPath',
  'userPath',
  'values',
  'vocabulary',
  'workspaceIds',
  'match',
  'unassigned',
  'other',
];

/**
 * Bring a stored rule into a shape the backend will accept on write. The array
 * is replaced wholesale on every save, so a single malformed rule otherwise
 * blocks every write — including the one that would delete it.
 *
 * `userPath` and `values` are exclusive peers; a rule carrying both is
 * ambiguous, so it resolves to `userPath` — the form the docs prefer.
 */
export const normalizeRule = rule => {
  const normalized = {};
  RULE_KEYS.forEach(key => {
    if (rule[key] !== undefined) normalized[key] = rule[key];
  });

  if (normalized.userPath && normalized.values) delete normalized.values;

  ['values', 'vocabulary', 'workspaceIds'].forEach(key => {
    if (Array.isArray(normalized[key]) && !normalized[key].length) delete normalized[key];
  });

  return normalized;
};

/**
 * Both exclusive peers carry real content. This is the only part of
 * `normalizeRule` that changes what a rule does — key order, `_id` and empty
 * arrays are cosmetic and not worth telling anyone about.
 */
export const hasTargetConflict = rule => Boolean(rule.userPath) && Boolean(rule.values?.length);

/** §7: a missing `search` key, a missing `rules` key and `[]` all mean "no rules". */
export const readStoredRules = organization =>
  organization?.internalConfigurations?.search?.rules ?? [];

export const readRules = organization => readStoredRules(organization).map(normalizeRule);

export const formatOutcome = outcome => {
  if (outcome === EXCLUDE) return EXCLUDE;
  const numeric = Number(outcome ?? 1);
  return numeric === 1 ? '—' : `×${numeric}`;
};

export const toRuleKey = raw =>
  raw
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
